import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StartupJourneySetup from '../../components/journey/StartupJourneySetup';
import BasePageLayout from '../../components/BasePageLayout';
import { JourneySetup } from '../../types/journey';
import { JourneyApiService } from '../../services/journeyApiService';
import { useAuth } from '../../context/AuthContext';

const StartupJourneySetupPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetupComplete = async (setup: JourneySetup) => {
    // Wait for auth to finish loading
    if (authLoading) {
      setError('Please wait while we complete your authentication...');
      
      // Wait up to 5 seconds for auth to load
      const maxWaitTime = 5000;
      const startTime = Date.now();
      while (authLoading && (Date.now() - startTime) < maxWaitTime) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    if (!user) {
      setError('You must be logged in to create a journey. Please sign in and try again.');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Call the actual API to create journey
      const response = await JourneyApiService.createJourney({ setup });
      
      console.log('Journey created successfully:', response);
      
      // Navigate to the new journey
      navigate(`/journey/${response.journey.id}`);
      
    } catch (error) {
      console.error('Failed to create journey:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to create journey. Please try again.';
      
      // Provide more helpful error messages
      if (errorMessage.includes('Authentication required')) {
        setError('Session expired. Please refresh the page and sign in again.');
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <BasePageLayout maxWidth="none">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#667eea'
        }}>
          Creating your personalized journey...
        </div>
      </BasePageLayout>
    );
  }

  // Show loading state while auth is initializing
  if (authLoading) {
    return (
      <BasePageLayout maxWidth="none">
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#667eea'
        }}>
          <div>Setting up your account...</div>
          <div style={{ fontSize: '14px', color: '#999' }}>This will only take a moment</div>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout maxWidth="none" background="gradient" padding="none">
      {error && (
        <div style={{
          backgroundColor: '#fee',
          color: '#c53030',
          padding: '1rem',
          margin: '1rem',
          borderRadius: '4px',
          border: '1px solid #feb2b2'
        }}>
          {error}
        </div>
      )}
      <StartupJourneySetup
        onComplete={handleSetupComplete}
        onCancel={handleCancel}
      />
    </BasePageLayout>
  );
};

export default StartupJourneySetupPage;